import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { globalErrorHandler } from "./globalErrorHandler";

interface ErrorState {
  isServerMaintenance: boolean;
  errorMessage?: string;
}

interface ErrorContextType {
  errorState: ErrorState;
  setServerMaintenance: (isActive: boolean, message?: string) => void;
  clearErrors: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

interface ErrorProviderProps {
  children: ReactNode;
}

export function ErrorProvider({ children }: ErrorProviderProps) {
  const [errorState, setErrorState] = useState<ErrorState>({
    isServerMaintenance: false,
  });

  const setServerMaintenance = (isActive: boolean, message?: string) => {
    setErrorState({
      isServerMaintenance: isActive,
      errorMessage: message,
    });
  };

  const clearErrors = () => {
    setErrorState({
      isServerMaintenance: false,
      errorMessage: undefined,
    });
  };

  // Register the callback with the global error handler
  useEffect(() => {
    globalErrorHandler.setServerMaintenanceCallback(setServerMaintenance);
  }, []);

  return (
    <ErrorContext.Provider
      value={{
        errorState,
        setServerMaintenance,
        clearErrors,
      }}
    >
      {children}
    </ErrorContext.Provider>
  );
}

export function useError() {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error("useError must be used within an ErrorProvider");
  }
  return context;
}
